import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronRight, Copy, Download, FileText, Folder, Globe2, Grid3X3, List,
  Lock, MoreHorizontal, Pencil, Plus, RefreshCw, Search, Settings2, Trash2, Upload,
} from 'lucide-react';
import { api, apiResponse, apiUpload, jsonBody } from './api';
import { Modal } from './Modal';
import { Select } from './Select';
import { ConfirmDialog } from './Dialogs';

type Visibility = 'inherit' | 'public' | 'private';
type Entry = {
  id: string; name: string; kind: 'file' | 'folder'; parent_id: string | null;
  path: string; visibility: Visibility; effectiveVisibility: 'public' | 'private';
  visibilitySource: { id: string; name: string }; size_bytes: number;
  content_type: string | null; etag: string; updated_at: string;
  breadcrumbs: Array<{ id: string; name: string }>; publicUrl: string | null;
  downloadMetadata?: { contentType?: string; filename?: string; disposition?: 'attachment' | 'inline' };
};
type Space = { id: string; visibility: 'public' | 'private'; version: number; etag?: string };
type Notify = (type: 'success' | 'error', message: string) => void;
const message = (error: unknown) => error instanceof Error ? error.message : '操作失败';
const bytes = (value: number) => value < 1024 ? `${value} B` : value < 1024 ** 2 ? `${(value / 1024).toFixed(1)} KiB` : `${(value / 1024 ** 2).toFixed(1)} MiB`;
const TEXT_LIMIT = 1024 * 1024;
const canEditText = (entry: Entry) => entry.kind === 'file' && entry.size_bytes <= TEXT_LIMIT && (
  /^text\//i.test(entry.content_type ?? '') ||
  /^(application\/(json|yaml|x-yaml|xml|javascript|x-sh|toml))(;|$)/i.test(entry.content_type ?? '') ||
  /\.(txt|md|json|ya?ml|toml|ini|conf|csv|log|xml|html?|css|js|ts|sh|py|go|rs|sql)$/i.test(entry.name)
);

export function GlobalFiles({ notify }: { notify: Notify }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [parent, setParent] = useState<Entry | null>(null);
  const [space, setSpace] = useState<Space | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [selected, setSelected] = useState<Entry | null>(null);
  const [dialog, setDialog] = useState<'folder' | 'properties' | 'space' | 'editor' | null>(null);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('inherit');
  const [metadata, setMetadata] = useState({ contentType: '', filename: '', disposition: 'attachment' as 'attachment' | 'inline' });
  const [editor, setEditor] = useState<{ entry: Entry; text: string; original: string; type: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Entry | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async (parentID = parent?.id ?? '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (parentID) params.set('parent_id', parentID);
      if (search) params.set('q', search);
      const [listing, info] = await Promise.all([
        api<{ items: Entry[]; parent: Entry | null }>(`/api/files${params.toString() ? `?${params}` : ''}`),
        api<Space>('/api/files/space'),
      ]);
      setEntries(listing.items);
      setParent(listing.parent);
      setSpace(info);
    } catch (error) { notify('error', message(error)); }
    finally { setLoading(false); }
  }, [notify, parent?.id, search]);

  useEffect(() => { void load(''); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [search]);

  const crumbs = parent?.breadcrumbs ?? [];

  async function uploadFiles(files: File[]) {
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        if (parent) form.append('parent_id', parent.id);
        form.append('name', file.name);
        await apiUpload('/api/files/upload', { method: 'POST', body: form }, (progress) => {
          if (progress.total) setUploading(`${file.name} · ${Math.round(progress.loaded / progress.total * 100)}%`);
        });
      }
      setUploading('');
      await load(parent?.id ?? '');
      notify('success', '文件已上传');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); setUploading(''); }
  }

  async function createFolder() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api('/api/files/folders', { method: 'POST', body: jsonBody({ name, parent_id: parent?.id ?? null, visibility }) });
      setDialog(null); setName(''); setVisibility('inherit');
      await load(parent?.id ?? '');
      notify('success', '文件夹已创建');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  async function updateEntry() {
    if (!selected) return;
    setBusy(true);
    try {
      await api(`/api/files/${selected.id}`, {
        method: 'PATCH', headers: { 'If-Match': selected.etag },
        body: jsonBody({ name, visibility, downloadMetadata: selected.kind === 'file' ? {
          contentType: metadata.contentType, filename: metadata.filename, disposition: metadata.disposition,
        } : undefined }),
      });
      setDialog(null);
      await load(parent?.id ?? '');
      notify('success', '文件属性已更新');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  async function updateSpace() {
    if (!space) return;
    setBusy(true);
    try {
      await api('/api/files/space', { method: 'PATCH', body: jsonBody({ visibility }) });
      setDialog(null);
      await load(parent?.id ?? '');
      notify('success', '空间权限已更新');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  function remove(entry: Entry) {
    setPendingDelete(entry);
  }

  async function confirmRemove() {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await api(`/api/files/${pendingDelete.id}`, { method: 'DELETE' });
      setPendingDelete(null);
      await load(parent?.id ?? '');
      notify('success', '已删除');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  async function openEditor(entry: Entry) {
    try {
      const response = await apiResponse(`/api/files/${entry.id}/content`);
      const text = await response.text();
      setEditor({ entry, text, original: text, type: entry.content_type || 'text/plain; charset=utf-8' });
      setDialog('editor');
    } catch (error) { notify('error', message(error)); }
  }

  async function saveEditor() {
    if (!editor || busy) return;
    setBusy(true);
    try {
      const result = await api<Entry>(`/api/files/${editor.entry.id}/content`, {
        method: 'PUT', headers: { 'content-type': editor.type }, body: editor.text,
      });
      setEditor({ entry: result, text: editor.text, original: editor.text, type: editor.type });
      await load(parent?.id ?? '');
      notify('success', '文本已保存');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  async function copyURL(entry: Entry) {
    if (!entry.publicUrl) return;
    try {
      await navigator.clipboard.writeText(new URL(entry.publicUrl, window.location.origin).href);
      notify('success', '公开 URL 已复制');
    } catch { notify('error', '无法复制 URL'); }
  }

  const itemLabel = useMemo(() => entries.length === 1 ? '1 项' : `${entries.length} 项`, [entries.length]);

  return <div className="page-stack tenant-page">
    <section className="panel file-toolbar">
      <form className="search-box" onSubmit={(event) => { event.preventDefault(); setSearch(query.trim()); }}>
        <Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索文件名" aria-label="搜索文件" />
      </form>
      <span>{space ? `空间默认：${space.visibility === 'public' ? '公开' : '私有'}` : '读取空间…'} · {itemLabel}</span>
      <div className="inline-actions">
        <button className="icon-button" title="空间权限" aria-label="空间权限" onClick={() => { if (space) { setVisibility(space.visibility); setDialog('space'); } }}><Settings2 size={17} /></button>
        <button className="secondary-button" onClick={() => { setName(''); setVisibility('inherit'); setDialog('folder'); }}><Plus size={16} />新建文件夹</button>
        <button className="primary-button" disabled={busy} onClick={() => fileInput.current?.click()}><Upload size={16} />上传文件</button>
        <input ref={fileInput} type="file" multiple hidden onChange={(event) => { void uploadFiles(Array.from(event.target.files ?? [])); event.currentTarget.value = ''; }} />
      </div>
      <div className="inline-actions">
        <button className={`icon-button ${view === 'list' ? 'selected' : ''}`} aria-label="列表视图" title="列表视图" onClick={() => setView('list')}><List size={17} /></button>
        <button className={`icon-button ${view === 'grid' ? 'selected' : ''}`} aria-label="图标视图" title="图标视图" onClick={() => setView('grid')}><Grid3X3 size={17} /></button>
        <button className="icon-button" aria-label="刷新" title="刷新" onClick={() => void load(parent?.id ?? '')}><RefreshCw size={17} /></button>
      </div>
    </section>

    {uploading && <div className="file-upload-bar" role="status"><Upload size={15} />{uploading}</div>}

    <nav className="file-breadcrumbs" aria-label="文件路径">
      <button type="button" disabled={busy} onClick={() => void load('')}>根目录</button>
      {crumbs.map((node) => <span key={node.id}><ChevronRight size={13} /><button type="button" disabled={busy} onClick={() => void load(node.id)}>{node.name}</button></span>)}
    </nav>

    <section className={`panel file-browser ${view}`}>
      {loading && <div className="panel-loader" role="status">正在加载文件…</div>}
      {!loading && entries.length === 0 && <div className="tenant-empty"><FileText size={28} /><strong>{search ? '没有匹配的文件' : '此目录为空'}</strong><span>{search ? '换个关键词。' : '上传文件或新建文件夹。'}</span></div>}
      {!loading && entries.length > 0 && view === 'list' && <div className="file-table">
        <div className="file-table-head"><span>名称</span><span>权限</span><span>大小</span><span>更新时间</span><span /></div>
        {entries.map((entry) => <div className="file-table-row" key={entry.id}>
          <button type="button" className="file-name" onClick={() => entry.kind === 'folder' ? void load(entry.id) : canEditText(entry) ? void openEditor(entry) : undefined}>
            {entry.kind === 'folder' ? <Folder size={17} /> : <FileText size={17} />}
            <span>{entry.name}{search && entry.path !== entry.name && <small>{entry.path}</small>}</span>
          </button>
          <span className={`file-access ${entry.effectiveVisibility}`}>{entry.effectiveVisibility === 'public' ? <Globe2 size={13} /> : <Lock size={13} />}{entry.effectiveVisibility === 'public' ? '公开' : '私有'}</span>
          <span>{entry.kind === 'folder' ? '—' : bytes(entry.size_bytes)}</span>
          <span>{new Date(entry.updated_at).toLocaleString('zh-CN', { hour12: false })}</span>
          <div className="file-actions">
            {entry.kind === 'file' && <button type="button" className="icon-button" title={entry.publicUrl ? '复制公开 URL' : '非公开文件'} aria-label="复制公开 URL" disabled={!entry.publicUrl} onClick={() => void copyURL(entry)}><Copy size={15} /></button>}
            {entry.kind === 'file' && <a className="icon-button" title="下载" aria-label="下载" href={`/api/files/${entry.id}/content`}><Download size={15} /></a>}
            <button type="button" className="icon-button" title="属性" aria-label="属性" onClick={() => { setSelected(entry); setName(entry.name); setVisibility(entry.visibility); setMetadata({ contentType: entry.downloadMetadata?.contentType || '', filename: entry.downloadMetadata?.filename || '', disposition: entry.downloadMetadata?.disposition || 'attachment' }); setDialog('properties'); }}><MoreHorizontal size={16} /></button>
          </div>
        </div>)}
      </div>}
      {!loading && entries.length > 0 && view === 'grid' && <div className="file-grid">
        {entries.map((entry) => <article className="file-card" key={entry.id}>
          <button type="button" className="file-card-open" onClick={() => entry.kind === 'folder' ? void load(entry.id) : canEditText(entry) ? void openEditor(entry) : undefined}>
            {entry.kind === 'folder' ? <Folder size={34} /> : <FileText size={34} />}<strong>{entry.name}</strong>
          </button>
          <div className="file-card-meta"><span className={`file-access ${entry.effectiveVisibility}`}>{entry.effectiveVisibility === 'public' ? '公开' : '私有'}</span><span>{entry.kind === 'folder' ? '文件夹' : bytes(entry.size_bytes)}</span></div>
          <div className="file-actions">
            {entry.kind === 'file' && <button type="button" className="icon-button" disabled={!entry.publicUrl} onClick={() => void copyURL(entry)}><Copy size={14} /></button>}
            {entry.kind === 'file' && <a className="icon-button" href={`/api/files/${entry.id}/content`}><Download size={14} /></a>}
            <button type="button" className="icon-button" onClick={() => { setSelected(entry); setName(entry.name); setVisibility(entry.visibility); setDialog('properties'); }}><MoreHorizontal size={15} /></button>
          </div>
        </article>)}
      </div>}
    </section>

    {dialog === 'folder' && <Modal title="新建文件夹" subtitle={parent ? `在 ${parent.path} 下` : '根目录'} onClose={() => setDialog(null)}>
      <form className="device-form" onSubmit={(event) => { event.preventDefault(); void createFolder(); }}>
        <label><span>名称</span><input autoFocus value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label><span>权限</span><Select aria-label="权限" value={visibility} onChange={(event) => setVisibility(event.target.value as Visibility)}><option value="inherit">继承空间</option><option value="private">私有</option><option value="public">公开</option></Select></label>
        <footer><button type="button" className="secondary-button" onClick={() => setDialog(null)}>取消</button><button type="submit" className="primary-button" disabled={busy || !name.trim()}>创建</button></footer>
      </form>
    </Modal>}

    {dialog === 'properties' && selected && <Modal title={selected.name} subtitle={selected.path} onClose={() => setDialog(null)}>
      <form className="device-form" onSubmit={(event) => { event.preventDefault(); void updateEntry(); }}>
        <label><span>名称</span><input value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label><span>权限</span><Select aria-label="权限" value={visibility} onChange={(event) => setVisibility(event.target.value as Visibility)}><option value="inherit">继承上级</option><option value="private">私有</option><option value="public">公开</option></Select></label>
        {selected.kind === 'file' && <>
          <label><span>Content-Type（下载）</span><input value={metadata.contentType} onChange={(event) => setMetadata({ ...metadata, contentType: event.target.value })} placeholder={selected.content_type || 'application/octet-stream'} /></label>
          <label><span>下载文件名</span><input value={metadata.filename} onChange={(event) => setMetadata({ ...metadata, filename: event.target.value })} placeholder={selected.name} /></label>
          <label><span>打开方式</span><Select aria-label="打开方式" value={metadata.disposition} onChange={(event) => setMetadata({ ...metadata, disposition: event.target.value as 'attachment' | 'inline' })}><option value="attachment">下载附件</option><option value="inline">浏览器预览</option></Select></label>
        </>}
        <p className="script-meta">实际权限：{selected.effectiveVisibility === 'public' ? '公开' : '私有'} · 来源：{selected.visibilitySource.name}</p>
        <footer>
          <button type="button" className="secondary-button" onClick={() => setDialog(null)}>取消</button>
          {selected.kind === 'file' && canEditText(selected) && <button type="button" className="secondary-button" onClick={() => void openEditor(selected)}><Pencil size={14} />编辑文本</button>}
          {selected.publicUrl && <button type="button" className="secondary-button" onClick={() => void copyURL(selected)}><Copy size={14} />复制 URL</button>}
          <button type="button" className="danger-button" disabled={busy} onClick={() => void remove(selected)}><Trash2 size={14} />删除</button>
          <button type="submit" className="primary-button" disabled={busy}>保存</button>
        </footer>
      </form>
    </Modal>}

    {dialog === 'space' && space && <Modal title="空间权限" subtitle="全局文件空间" onClose={() => setDialog(null)}>
      <form className="device-form" onSubmit={(event) => { event.preventDefault(); void updateSpace(); }}>
        <label><span>默认权限</span><Select aria-label="默认权限" value={visibility === 'inherit' ? space.visibility : visibility} onChange={(event) => setVisibility(event.target.value as Visibility)}><option value="private">私有</option><option value="public">公开</option></Select></label>
        <p className="script-meta">空间默认权限会影响没有单独设置权限的文件和文件夹。</p>
        <footer><button type="button" className="secondary-button" onClick={() => setDialog(null)}>取消</button><button type="submit" className="primary-button" disabled={busy}>保存</button></footer>
      </form>
    </Modal>}

    {dialog === 'editor' && editor && <Modal title={`编辑：${editor.entry.path}`} subtitle="UTF-8 · 最多 1 MiB" onClose={() => setDialog(null)}>
      <div className="file-editor-dialog">
        <div className="script-toolbar"><span className="script-meta">{editor.text !== editor.original ? '有未保存修改' : '已保存'} · {bytes(new TextEncoder().encode(editor.text).length)}</span><button type="button" className="primary-button small" disabled={busy} onClick={() => void saveEditor()}>保存</button></div>
        <textarea value={editor.text} onChange={(event) => setEditor({ ...editor, text: event.target.value })} spellCheck={false} onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') { event.preventDefault(); void saveEditor(); } }} />
      </div>
    </Modal>}

    {pendingDelete && <ConfirmDialog
      title="删除文件"
      message={`删除“${pendingDelete.name}”？文件夹必须为空。`}
      confirmLabel="删除"
      danger
      busy={busy}
      onConfirm={() => void confirmRemove()}
      onCancel={() => setPendingDelete(null)}
    />}
  </div>;
}
